import { useCallback, useRef, useState } from "react";

const MAX_ENTRIES = 200;

const storageKey = (scope: string) => `deviceShell.history.${scope}`;

const readHistory = (scope: string): string[] => {
  try {
    const raw = window.localStorage.getItem(storageKey(scope));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === "string") : [];
  } catch {
    // A corrupt or unavailable localStorage must never stop the console from opening.
    return [];
  }
};

const writeHistory = (scope: string, entries: string[]) => {
  try {
    window.localStorage.setItem(storageKey(scope), JSON.stringify(entries));
  } catch {
    // Ignore quota / private-mode failures: history is a convenience, not a requirement.
  }
};

export type CommandHistory = {
  entries: string[];
  /** Record a command. Consecutive duplicates are collapsed. */
  push: (command: string) => void;
  /** Step towards older entries. Returns the command to show, or `undefined` at the oldest. */
  previous: () => string | undefined;
  /** Step towards newer entries. Returns the command to show, or `""` once past the newest. */
  next: () => string | undefined;
  /** Reset the cursor, e.g. after submitting a command. */
  reset: () => void;
};

/**
 * useCommandHistory keeps a per-storage command history, persisted in localStorage, and a cursor
 * for walking it with the up/down arrow keys. The cursor counts back from the newest entry, with
 * -1 meaning "not browsing history".
 */
export const useCommandHistory = (scope: string): CommandHistory => {
  const [entries, setEntries] = useState<string[]>(() => readHistory(scope));
  const cursor = useRef(-1);
  // The cursor reads entries synchronously between renders, so it needs the live list.
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  const push = useCallback(
    (command: string) => {
      const trimmed = command.trim();
      if (!trimmed) return;
      const current = entriesRef.current;
      if (current[current.length - 1] === trimmed) return;
      const updated = [...current, trimmed].slice(-MAX_ENTRIES);
      entriesRef.current = updated;
      setEntries(updated);
      writeHistory(scope, updated);
    },
    [scope],
  );

  const previous = useCallback(() => {
    const current = entriesRef.current;
    if (cursor.current + 1 >= current.length) return undefined;
    cursor.current += 1;
    return current[current.length - 1 - cursor.current];
  }, []);

  const next = useCallback(() => {
    const current = entriesRef.current;
    if (cursor.current <= 0) {
      cursor.current = -1;
      return "";
    }
    cursor.current -= 1;
    return current[current.length - 1 - cursor.current];
  }, []);

  const reset = useCallback(() => {
    cursor.current = -1;
  }, []);

  return { entries, push, previous, next, reset };
};
